export type ResizeImageOptions = {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  /** 0 = her zaman işle (admin önizleme). Varsayılan: 350_000 */
  skipIfSmallerThanBytes?: number;
  /** true ise hedef boyuta küçültme + yeniden kodlama zorunlu */
  forceReencode?: boolean;
};

export type ImageDimensions = { width: number; height: number };

async function loadBitmap(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file);
  } catch {
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise<HTMLImageElement>((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('Görsel okunamadı'));
        el.src = url;
      });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Canvas desteklenmiyor');
      ctx.drawImage(img, 0, 0);
      return await createImageBitmap(canvas);
    } finally {
      URL.revokeObjectURL(url);
    }
  }
}

export async function getImageFileDimensions(file: File): Promise<ImageDimensions | null> {
  if (!file.type.startsWith('image/')) return null;
  try {
    const bitmap = await loadBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  } catch {
    return null;
  }
}

function outputMime(file: File): string {
  if (file.type === 'image/png') return 'image/png';
  if (file.type === 'image/webp') return 'image/webp';
  return 'image/jpeg';
}

function extForMime(mime: string): string {
  if (mime === 'image/png') return 'png';
  if (mime === 'image/webp') return 'webp';
  return 'jpg';
}

/** Tarayıcıda görseli yeniden boyutlandırır (yüklemeyi hafifletir). */
export async function resizeImageFile(file: File, options: ResizeImageOptions = {}): Promise<File> {
  const maxWidth = options.maxWidth ?? 1280;
  const maxHeight = options.maxHeight ?? 800;
  const quality = options.quality ?? 0.85;
  const skipBytes = options.skipIfSmallerThanBytes ?? 350_000;

  if (!file.type.startsWith('image/')) return file;

  const bitmap = await loadBitmap(file);
  const { width, height } = bitmap;
  const scale = Math.min(maxWidth / width, maxHeight / height, 1);
  const targetW = Math.max(1, Math.round(width * scale));
  const targetH = Math.max(1, Math.round(height * scale));

  if (
    !options.forceReencode &&
    skipBytes > 0 &&
    scale >= 1 &&
    targetW === width &&
    targetH === height &&
    file.size <= skipBytes
  ) {
    bitmap.close();
    return file;
  }

  const canvas = document.createElement('canvas');
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    return file;
  }

  if (file.type === 'image/png') {
    ctx.clearRect(0, 0, targetW, targetH);
  }

  ctx.drawImage(bitmap, 0, 0, targetW, targetH);
  bitmap.close();

  const mime = outputMime(file);
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, mime, mime === 'image/png' ? undefined : quality);
  });

  if (!blob) return file;

  const base = file.name.replace(/\.[^.]+$/i, '') || 'image';
  return new File([blob], `${base}.${extForMime(mime)}`, { type: mime, lastModified: Date.now() });
}

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}
